import { FC, Fragment } from "react";
import { Box } from "@mui/material";
import { BillingExplorerWidgetContentCosts } from "../../BillingExplorerWidgetContentCosts";
import { useAppSelector } from "@/store/hooks";
import { DateLabelNullable } from "@/services/cloudchipr.api";
import { billingExplorerWidgetTableViewDataSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetTableViewDataSelector";
import { billingExplorerWidgetVisualizationSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetVisualizationSelector";
import { billingExplorerWidgetGroupingSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetGroupingSelector";
import { billingExplorerWidgetForecastOptionSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetForecastOptionSelector";
import { billingExplorerWidgetHasAggregationSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetHasAggregationSelector";
import { BillingExplorerWidgetAgGrid } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/BillingExplorerWidgetAgGrid";
import { billingExplorerWidgetByWidgetIdLoadingSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/loading/billingExplorerWidgetByWidgetIdLoadingSelector";
import { billingExplorerWidgetTableTrendSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetTableTrendSelector";
import { billingExplorerWidgetPrevTotalCostSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetPrevTotalCostSelector";
import { billingExplorerWidgetForecastedCostsSumSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetForecastedCostsSumSelector";
import { billingExplorerWidgetTotalCostSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetTotalCostSelector";
import { billingExplorerWidgetTableDataCountSelector } from "@/store/dashboards/selectors/widgets/billing-explorer-widget/widget-view/billingExplorerWidgetTableDataCountSelector";

interface BillingExplorerWidgetTableViewProps {
  widgetId: string;
  dateLabel?: DateLabelNullable;
}

export const BillingExplorerWidgetTableView: FC<
  BillingExplorerWidgetTableViewProps
> = ({ widgetId, dateLabel }) => {
  const loading = useAppSelector((state) =>
    billingExplorerWidgetByWidgetIdLoadingSelector(state, widgetId),
  );

  const gridData =
    useAppSelector((state) =>
      billingExplorerWidgetTableViewDataSelector(state, widgetId),
    ) ?? [];
  const visualization = useAppSelector((state) =>
    billingExplorerWidgetVisualizationSelector(state, widgetId),
  );

  const totalTrend = useAppSelector((state) =>
    billingExplorerWidgetTableTrendSelector(state, widgetId),
  );

  const grouping = useAppSelector((state) =>
    billingExplorerWidgetGroupingSelector(state, widgetId),
  );

  const forecastOption = useAppSelector((state) =>
    billingExplorerWidgetForecastOptionSelector(state, widgetId),
  );

  const prevCostDates = useAppSelector((state) =>
    billingExplorerWidgetPrevTotalCostSelector(state, widgetId),
  )?.dateDetails;

  const totalCostDates = useAppSelector((state) =>
    billingExplorerWidgetTotalCostSelector(state, widgetId),
  )?.dateDetails;

  const forecastedCost = useAppSelector((state) =>
    billingExplorerWidgetForecastedCostsSumSelector(state, widgetId),
  );

  const totalItemsCount = useAppSelector((state) =>
    billingExplorerWidgetTableDataCountSelector(state, widgetId),
  );

  const hasAggregations = useAppSelector((state) =>
    billingExplorerWidgetHasAggregationSelector(state, widgetId),
  );

  return (
    <Fragment>
      {hasAggregations && (
        <Box p={2}>
          <BillingExplorerWidgetContentCosts
            widgetId={widgetId}
            dateLabel={dateLabel}
          />
        </Box>
      )}
      <BillingExplorerWidgetAgGrid
        data={gridData}
        loading={loading}
        grouping={grouping}
        totalTrend={totalTrend}
        prevCostDates={prevCostDates}
        visualization={visualization}
        forecastOption={forecastOption}
        forecastedCost={forecastedCost}
        totalCostDates={totalCostDates}
        totalItemsCount={totalItemsCount}
      />
    </Fragment>
  );
};
